import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
// Calendar schemas
// Sync provider metadata attached to a calendar when it is linked to an external
// provider (e.g. Google). Calendars without this are considered non-sync calendars.
const calendarSyncDataSchema = z.object({
    syncProvider: z.string().optional(),
    // Absent on a calendar with a sync provider means a read calendar
    direction: z.enum(['Read', 'Write']).optional(),
    // On a write calendar: false when writing to the provider is off (the provider calendar is kept)
    writeEnabled: z.boolean().optional(),
    lastSyncedAt: z.coerce.date().optional(),
    externalId: z.string().optional(),
});
const calendarSchema = z.object({
    id: z.string(),
    userId: z.string(),
    name: z.string(),
    color: z.string().optional(),
    syncData: calendarSyncDataSchema.optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
const createCalendarResponseSchema = z.object({
    calendarId: z.string(),
});
// Calendar Entry schemas
// Recurrence schema for API responses (database format with rrule string)
const recurrenceSchema = z
    .object({
    rrule: z.string().optional().nullable(),
    disabled: z.boolean().optional(),
    exclusions: z.array(z.string()).optional(),
    lastOccurrence: z.string().optional().nullable(),
})
    .optional();
const calendarEntrySchema = z.object({
    id: z.string(),
    userId: z.string(),
    calendarId: z.string(),
    title: z.string(),
    description: z.string().optional().nullable(),
    scheduledAt: z.coerce.date(),
    scheduledEnd: z.coerce.date(),
    localStart: z.string(),
    originalTimezoneOffset: z.number().optional().nullable(),
    recurrence: recurrenceSchema.nullable(),
    isAllDay: z.boolean().optional(),
    allDayStartDate: z.string().optional().nullable(),
    allDayEndDate: z.string().optional().nullable(),
    duration: z.number(),
    labels: z.array(z.string()),
    syncData: z.any().optional(),
    timeZone: z.string(),
    parentSeriesId: z.string().optional().nullable(),
    seriesGroupId: z.string().optional().nullable(),
    effectiveStartDate: z.coerce.date().optional().nullable(),
    effectiveEndDate: z.coerce.date().optional().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
const calendarOccurrenceSchema = z.object({
    seriesId: z.string(),
    occurrenceId: z.string(),
    startTime: z.coerce.date(),
    endTime: z.coerce.date(),
    isOverridden: z.boolean(),
    userId: z.string(),
    calendarId: z.string(),
    title: z.string(),
    description: z.string().optional().nullable(),
    scheduledAt: z.coerce.date().optional().nullable(),
    localStart: z.string().optional().nullable(),
    originalTimezoneOffset: z.number().optional().nullable(),
    recurrence: recurrenceSchema.nullable(),
    isAllDay: z.boolean().optional(),
    allDayStartDate: z.string().optional().nullable(),
    allDayEndDate: z.string().optional().nullable(),
    duration: z.number().optional().nullable(),
    labels: z.array(z.string()),
    syncData: z.unknown().optional(),
    timeZone: z.string().optional().nullable(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
});
const createCalendarEntryResponseSchema = z.object({
    calendarEntryId: z.string(),
});
// Calendar hooks
/**
 * Get all calendars for the authenticated user
 */
export const useGetUserCalendars = (authenticatedRequest, autoFetch = true) => {
    return useQuery({
        queryKey: ['calendars'],
        queryFn: async () => {
            const response = await authenticatedRequest({ url: '/calendars', method: 'GET' }, z.array(calendarSchema));
            return response;
        },
        enabled: autoFetch,
    });
};
/**
 * Get a specific calendar by ID
 */
export const useGetCalendar = (calendarId, authenticatedRequest, autoFetch = true) => {
    return useQuery({
        queryKey: ['calendar', calendarId],
        queryFn: async () => {
            const response = await authenticatedRequest({ url: `/calendars/${calendarId}`, method: 'GET' }, calendarSchema);
            return response;
        },
        enabled: autoFetch && !!calendarId,
    });
};
/**
 * Create a new calendar
 */
export const useCreateCalendar = (authenticatedRequest) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => {
            return authenticatedRequest({
                url: '/calendars',
                method: 'POST',
                data,
            }, createCalendarResponseSchema);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['calendars'] });
        },
    });
};
/**
 * Update a calendar
 */
export const useUpdateCalendar = (authenticatedRequest) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ calendarId, data }) => {
            return authenticatedRequest({
                url: `/calendars/${calendarId}`,
                method: 'PUT',
                data,
            }, z.object({ message: z.string() }));
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['calendars'] });
            queryClient.invalidateQueries({ queryKey: ['calendar', variables.calendarId] });
        },
    });
};
/**
 * Delete a calendar
 */
export const useDeleteCalendar = (authenticatedRequest) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (calendarId) => {
            return authenticatedRequest({
                url: `/calendars/${calendarId}`,
                method: 'DELETE',
            }, z.object({ success: z.boolean() }));
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['calendars'] });
        },
    });
};
// Calendar Entry hooks
/**
 * Get calendar entry occurrences for a date range
 * This is the primary method for fetching events to display on the calendar
 */
export const useGetCalendarEntryOccurrences = (params, authenticatedRequest, autoFetch = true) => {
    return useQuery({
        queryKey: [
            'calendarOccurrences',
            params.startDate,
            params.endDate,
            params.timeZone,
            params.calendarIds,
        ],
        queryFn: async () => {
            const queryParams = new URLSearchParams({
                startDate: params.startDate,
                endDate: params.endDate,
                timeZone: params.timeZone,
            });
            if (params.calendarIds && params.calendarIds.length > 0) {
                params.calendarIds.forEach((id) => queryParams.append('calendarIds', id));
            }
            const response = await authenticatedRequest({
                url: `/calendars/entries/occurrences?${queryParams.toString()}`,
                method: 'GET',
            }, z.array(calendarOccurrenceSchema));
            return response;
        },
        enabled: autoFetch && !!params.startDate && !!params.endDate && !!params.timeZone,
    });
};
/**
 * Get a single calendar entry by ID
 */
export const useGetCalendarEntry = (entryId, authenticatedRequest, autoFetch = true) => {
    return useQuery({
        queryKey: ['calendarEntry', entryId],
        queryFn: async () => {
            const response = await authenticatedRequest({ url: `/calendars/entries/${entryId}`, method: 'GET' }, calendarEntrySchema);
            return response;
        },
        enabled: autoFetch && !!entryId,
    });
};
/**
 * Get all calendar entries for the authenticated user
 */
export const useGetUserCalendarEntries = (params, authenticatedRequest, autoFetch = true) => {
    return useQuery({
        queryKey: ['calendarEntries', params],
        queryFn: async () => {
            const queryParams = new URLSearchParams();
            if (params?.calendarId)
                queryParams.append('calendarId', params.calendarId);
            if (params?.startDate)
                queryParams.append('startDate', params.startDate);
            if (params?.endDate)
                queryParams.append('endDate', params.endDate);
            if (params?.timeZone)
                queryParams.append('timeZone', params.timeZone);
            if (params?.isAllDay !== undefined)
                queryParams.append('isAllDay', params.isAllDay.toString());
            if (params?.labels) {
                params.labels.forEach((label) => queryParams.append('labels', label));
            }
            const url = `/calendars/entries${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
            const response = await authenticatedRequest({ url, method: 'GET' }, z.array(calendarEntrySchema));
            return response;
        },
        enabled: autoFetch && !!authenticatedRequest,
    });
};
/**
 * Create a new calendar entry
 */
export const useCreateCalendarEntry = (authenticatedRequest) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ calendarId, data }) => {
            return authenticatedRequest({
                url: `/calendars/${calendarId}/entries`,
                method: 'POST',
                data,
            }, createCalendarEntryResponseSchema);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['calendarEntries'] });
            queryClient.invalidateQueries({ queryKey: ['calendarOccurrences'] });
        },
    });
};
/**
 * Update a calendar entry
 */
export const useUpdateCalendarEntry = (authenticatedRequest) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ entryId, data }) => {
            return authenticatedRequest({
                url: `/calendars/entries/${entryId}`,
                method: 'PUT',
                data,
            }, z.object({ message: z.string() }));
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['calendarEntries'] });
            queryClient.invalidateQueries({ queryKey: ['calendarOccurrences'] });
        },
    });
};
/**
 * Delete a calendar entry
 */
export const useDeleteCalendarEntry = (authenticatedRequest) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (entryId) => {
            return authenticatedRequest({
                url: `/calendars/entries/${entryId}`,
                method: 'DELETE',
            }, z.object({ success: z.boolean() }));
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['calendarEntries'] });
            queryClient.invalidateQueries({ queryKey: ['calendarOccurrences'] });
        },
    });
};
